import { FC, useMemo } from 'react';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Heading } from 'src/components/common/Heading';
import { ProductDTO } from 'src/models/pim';
import { ProductAttributeCode } from 'src/models/pim/attributes';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import styled from 'styled-components';
import { media } from 'src/styles/designs';
import { Paragraph } from 'src/components/common/typography/Text';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { head } from 'lodash';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { useTranslate } from 'react-t7e';
import { ProductPageProtocolsSessionsItem } from 'src/modules/products/components/ProductPage/ProductPageProtocolsSessions/ProductPageProtocolsSessionsItem';
import { StickyMenuSection } from 'src/components/common/StickyMenu/StickyMenuSection';

const Section = styled(StickyMenuSection)`
  padding: 50px 0;
  background: ${({ theme }) => theme.palette.background};

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 80px 0;
  }
`;

const SectionContainer = styled.div`
  & + & {
    margin-top: 56px;
  }
`;

const SectionHeading = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.text.palette.accent};
  text-transform: uppercase;
  margin: 42px 0 28px;
`;

const StepsContainer = styled.div`
  display: grid;
  gap: 40px;
  margin-top: 24px;

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 24px;
  }
`;

export const ProductPageProtocolsSessions: FC<ProductDTO> = (product) => {
  const translateFn = useTranslateItem();
  const { _ } = useTranslate();

  const { getProductAttribute, getMultipleProductAttributes } =
    useProductAttributes(product.attributes);

  const subtitle = head(
    getProductAttribute(ProductAttributeCode.ProtocolsSessionsText),
  );

  const sections = useMemo(
    () =>
      getMultipleProductAttributes(ProductAttributeCode.ProtocolsSessionsGroup)
        ?.map((attribute) => {
          const steps =
            getMultipleProductAttributes(
              ProductAttributeCode.ProtocolsSessionsGroupStepGroup,
              attribute,
            ) ?? [];

          return {
            title: head(
              getProductAttribute(
                ProductAttributeCode.ProtocolsSessionsGroupTitle,
                attribute,
              ),
            ),
            steps: steps.map((step) => ({
              title: head(
                getProductAttribute(
                  ProductAttributeCode.ProtocolsSessionsGroupStepGroupTitle,
                  step,
                ),
              ),
              description: head(
                getProductAttribute(
                  ProductAttributeCode.ProtocolsSessionsGroupStepGroupText,
                  step,
                ),
              ),
              items: getProductAttribute(
                ProductAttributeCode.ProtocolsSessionsGroupStepGroupProduct,
                step,
              ),
            })),
          };
        })
        .filter((d) => !!d.title) ?? [],
    [getProductAttribute, getMultipleProductAttributes],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <Section
      id="steps"
      label={_('Protocol steps')}
      index={2}
      threshold={{
        desktop: 0.3,
        mobile: 0.8,
      }}
    >
      <ConstraintContainer>
        <Heading>
          {translateFn(product).name} {_('Protocol')}
          {subtitle && (
            <Paragraph>
              <Markdown>{translateFn(subtitle).value}</Markdown>
            </Paragraph>
          )}
        </Heading>

        {sections.map((section, index) => (
          <SectionContainer key={index}>
            {section.title && (
              <SectionHeading>
                <Markdown>{translateFn(section.title).value}</Markdown>
              </SectionHeading>
            )}

            <StepsContainer>
              {section.steps.map((item, i) => (
                <ProductPageProtocolsSessionsItem
                  key={i}
                  index={i + 1}
                  {...item}
                />
              ))}
            </StepsContainer>
          </SectionContainer>
        ))}
      </ConstraintContainer>
    </Section>
  );
};
